import { useMemo, useState } from 'react';
import { Table } from '@/components';
import type { ColumnConfig } from '@/components/table/Table';
import { MapModal } from '@/components/maps';
import type { MarkerData } from '@/components/maps';
import { ShippingApi } from '../models';
import {
  getShippingDetailsFirstTableColumns,
  getShippingDetailsSecondTableColumns,
  getShippingDetailsThirdTableColumns,
} from '../config/shippingDetailsColumns';
interface ShippingDetailsProps {
  shipping: ShippingApi;
  hiddenFields?: string[];
  mergeSecondAndThirdTables?: boolean;
  originClickable?: boolean;
  originAddressOverride?: string;
}

const ShippingDetails = ({
  shipping,
  hiddenFields,
  mergeSecondAndThirdTables = false,
  originClickable = true,
  originAddressOverride,
}: ShippingDetailsProps) => {
  const [showDestinationMapModal, setShowDestinationMapModal] = useState(false);
  const [showOriginMapModal, setShowOriginMapModal] = useState(false);
  
  const handleOpenDestinationMap = () => {
    setShowDestinationMapModal(true);
  };

  const handleCloseDestinationMap = () => {
    setShowDestinationMapModal(false);
  };

  const handleOpenOriginMap = () => {
    setShowOriginMapModal(true);
  };

  const handleCloseOriginMap = () => {
    setShowOriginMapModal(false);
  };

  const getFullAddress = (address?: any) => {
    if (!address) return 'N/A';
    const parts = [
      address.streetName,
      address.doorNumber,
      address.apartmentNumber,
    ].filter(Boolean);
    return parts.join(' ') || 'N/A';
  };

  const hiddenFieldsSet = useMemo(
    () => new Set(hiddenFields ?? []),
    [hiddenFields]
  );

  const filterColumns = (columns: ColumnConfig[]) =>
    hiddenFieldsSet.size === 0
      ? columns
      : columns.filter((column) => !hiddenFieldsSet.has(column.field));

  const firstTableColumns = useMemo(
    () =>
      filterColumns(
        getShippingDetailsFirstTableColumns(shipping, handleOpenDestinationMap)
      ),
    [shipping, hiddenFieldsSet]
  );

  const secondTableColumns = useMemo(
    () =>
      filterColumns(
        getShippingDetailsSecondTableColumns(
          shipping,
          originClickable ? handleOpenOriginMap : undefined,
          originAddressOverride
        )
      ),
    [shipping, hiddenFieldsSet, originClickable, originAddressOverride]
  );

  const thirdTableColumns = useMemo(
    () => filterColumns(getShippingDetailsThirdTableColumns(shipping)),
    [shipping, hiddenFieldsSet]
  );

  const secondTableRenderColumns = mergeSecondAndThirdTables
    ? [...secondTableColumns, ...thirdTableColumns]
    : secondTableColumns;

  const firstTableData = [{ id: shipping.number }];
  const secondTableData = [{ externalId: shipping.commerceShippingNumber }];
  const thirdTableData = [{ id: shipping.number }];

  const deliveryMarker: MarkerData[] = useMemo(() => {
    const lat = Number(shipping.destinationAddress?.lat);
    const lng = Number(shipping.destinationAddress?.long);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [
        {
          lat,
          lng,
          label: `${getFullAddress(shipping.destinationAddress)} - ${shipping.destinationNeighborhood?.name || ''}`,
        },
      ];
    }
    return [];
  }, [shipping]);

  const originMarker: MarkerData[] = useMemo(() => {
    const lat = Number(shipping.originAddress?.lat);
    const lng = Number(shipping.originAddress?.long);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [
        {
          lat,
          lng,
          label: `${getFullAddress(shipping.originAddress)} - ${shipping.originNeighborhood?.name || ''}`,
        },
      ];
    }
    return [];
  }, [shipping]);

  return (
    <div className='space-y-6'>
      <Table
        columns={firstTableColumns}
        data={firstTableData}
        isLoading={false}
      />

      <Table
        columns={secondTableRenderColumns}
        data={secondTableData}
        isLoading={false}
      />

      {!mergeSecondAndThirdTables && (
        <Table
          columns={thirdTableColumns}
          data={thirdTableData}
          isLoading={false}
        />
      )}

      <MapModal
        open={showDestinationMapModal}
        onClose={handleCloseDestinationMap}
        title='Dirección de Entrega'
        markers={deliveryMarker}
      />

      <MapModal
        open={showOriginMapModal}
        onClose={handleCloseOriginMap}
        title='Dirección de Origen'
        markers={originMarker}
      />
    </div>
  );
};

export default ShippingDetails;
